import { WorkflowStatus, JSONObject } from "../services";
import { STATUS_LABELS, STATUS_DESCRIPTIONS } from "../constants";

export type CloudscapeStatusType = "success" | "error" | "warning" | "info" | "in-progress" | "stopped" | "pending";

export interface StatusInfo {
  label: string;
  type: CloudscapeStatusType;
  description?: string;
}

export class StatusMapper {
  private static readonly WORKFLOW_STATUS_MAP: Record<WorkflowStatus, CloudscapeStatusType> = {
    [WorkflowStatus.SUCCEEDED]: "success",
    [WorkflowStatus.FAILED]: "error",
    [WorkflowStatus.RUNNING]: "in-progress",
    [WorkflowStatus.TIMED_OUT]: "warning",
    [WorkflowStatus.ABORTED]: "warning",
  };

  private static readonly REGISTRATION_STATUS_MAP: Record<string, StatusInfo> = {
    REGISTERED: { label: STATUS_LABELS.REGISTERED, type: "success", description: STATUS_DESCRIPTIONS.REGISTERED },
    REGISTRATION_PENDING: { label: STATUS_LABELS.PENDING, type: "pending", description: STATUS_DESCRIPTIONS.PENDING },
    REGISTRATION_FAILED: { label: STATUS_LABELS.FAILED, type: "error", description: STATUS_DESCRIPTIONS.FAILED },
    REGISTRATION_REJECTED: { label: STATUS_LABELS.REJECTED, type: "warning", description: STATUS_DESCRIPTIONS.REJECTED },
    CANCELLED: { label: STATUS_LABELS.CANCELLED, type: "warning", description: STATUS_DESCRIPTIONS.CANCELLED },
  };

  static mapWorkflowStatus(status: WorkflowStatus): CloudscapeStatusType {
    return this.WORKFLOW_STATUS_MAP[status] ?? "info";
  }

  static getRegistrationStatus(abRegistration: JSONObject | null): StatusInfo {
    if (!abRegistration) {
      return { label: STATUS_LABELS.UNKNOWN, type: "stopped" };
    }

    const status = String(abRegistration.status || abRegistration.Status || "");
    return this.REGISTRATION_STATUS_MAP[status] || { label: status || STATUS_LABELS.UNKNOWN, type: "info" };
  }

  static getShumaStatus(rfd: JSONObject | null): StatusInfo {
    if (!rfd) {
      return { label: STATUS_LABELS.UNKNOWN, type: "stopped" };
    }

    const isShuma = rfd.isShuma ?? rfd.IsShuma;
    if (isShuma === true || isShuma === 1) {
      return { label: STATUS_LABELS.SHUMA, type: "success", description: STATUS_DESCRIPTIONS.SHUMA };
    }
    if (isShuma === false || isShuma === 0) {
      return { label: STATUS_LABELS.NOT_SHUMA, type: "info" };
    }
    return { label: STATUS_LABELS.UNKNOWN, type: "stopped" };
  }

  static getGuestStatus(abGuest: JSONObject | null): StatusInfo {
    if (!abGuest) {
      return { label: STATUS_LABELS.NOT_GUEST, type: "info", description: STATUS_DESCRIPTIONS.NOT_GUEST };
    }

    const isGuest = abGuest.guest ?? abGuest.Guest;
    if (isGuest === true || isGuest === 1) {
      return { label: STATUS_LABELS.GUEST, type: "success", description: STATUS_DESCRIPTIONS.GUEST };
    }
    return { label: STATUS_LABELS.NOT_GUEST, type: "info", description: STATUS_DESCRIPTIONS.NOT_GUEST };
  }

  static getInternalStatus(abRegistration: JSONObject | null): StatusInfo {
    if (!abRegistration) {
      return { label: STATUS_LABELS.NOT_INTERNAL, type: "info", description: STATUS_DESCRIPTIONS.NOT_INTERNAL };
    }

    const isInternal = abRegistration.internalAccount ?? abRegistration.InternalAccount;
    if (isInternal === true || isInternal === 1) {
      return { label: STATUS_LABELS.INTERNAL, type: "warning", description: STATUS_DESCRIPTIONS.INTERNAL };
    }
    return { label: STATUS_LABELS.NOT_INTERNAL, type: "info", description: STATUS_DESCRIPTIONS.NOT_INTERNAL };
  }
}
